import { NextPageContext } from 'next'
import { ApolloClient } from '@apollo/client'
import { captureException } from 'utils/Sentry'
import { STATUS_CODES } from 'utils/Constants'

export type MiddlewareResult = {
  isServerSideRedirect: boolean
  errorCode?: number
}

export type MiddlewareData = {
  nextContext: NextPageContext
  apolloClient: ApolloClient<any>
  data?: any
}

export type ExecuteMiddlewareFunction = {
  (data: MiddlewareData): Promise<MiddlewareResult | void>
}

export type MiddlewareFunction = {
  (data: MiddlewareData, next: () => void): Promise<MiddlewareResult | void>
}

/**
 * This mimics how a middleware stack would work in express.
 * You create middleware function by passing an array of middleware functions.
 * Each middleware function when called has a chance to return, which will exit
 * the middleware call chain, or call next() to run the next middleware function.
 */
export const getMiddleware =
  (middlewareFns: MiddlewareFunction[] = []): ExecuteMiddlewareFunction =>
  async (data: MiddlewareData): Promise<MiddlewareResult | void> => {
    const { nextContext } = data
    const isServerSide = !!(nextContext?.req && nextContext?.res && nextContext?.req?.url)

    if (!isServerSide) {
      return
    }

    let curMiddlewareIndex = 0
    let results = []
    const firstMiddleware = middlewareFns[0]

    const next = async () => {
      curMiddlewareIndex++
      const nextMiddleware = middlewareFns[curMiddlewareIndex]
      if (nextMiddleware) {
        try {
          const nextResult = await nextMiddleware(data, next)
          results.push(nextResult)
        } catch (e) {
          captureException(e)
          next()
        }
      }
    }

    if (firstMiddleware) {
      try {
        const firstResult = await firstMiddleware(data, next)
        results.push(firstResult)
      } catch (e) {
        captureException(e)
        next()
      }
    }

    const result = results.find((item) => typeof item !== 'undefined')

    return result
  }

export const handleUrlTypoRedirect: MiddlewareFunction = async (data, next) => {
  const { nextContext } = data
  const { req, res } = nextContext
  const errorCode = res && res.statusCode > 200 ? res.statusCode : null

  if (errorCode === STATUS_CODES.CODE404) {
    /**
     * Replaces slashes
     * Test vectors:
     * `/test/test/` -> `/test/test`
     * `/test/////test////` -> `/test/test`
     * `/test//test//?a=1&b=2` -> `/test?a=1&b=2`
     * `/test///#test` -> `/test#test`
     */
    const correctPath = (invalidPath) =>
      invalidPath
        .replace(/\/+$/, '')
        .replace(/\/+#/, '#')
        .replace(/\/+\?/, '?')
        .replace(/\/+/g, '/')

    if (correctPath(req.url) !== req.url) {
      // Do a 301 Permenant redirect to correct path.
      res.writeHead(STATUS_CODES.CODE301, { Location: correctPath(req.url) })
      res.end()

      return {
        isServerSideRedirect: true,
        errorCode: null,
      }
    } else {
      return next()
    }
  } else {
    return next()
  }
}
